import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Key, Trash2, Shield, ShieldOff, Search, Home as HomeIcon, ChevronRight, LogOut } from 'react-feather';
import config from '../../../config';
import CustomizerContext from '../../../_helper/Customizer';
import './backend-home.css';

const greetingFor = (date = new Date()) => {
    const h = date.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

const BackendHomeView = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: token } };
    const navigate = useNavigate();
    const { layoutURL } = useContext(CustomizerContext);
    const login_id = localStorage.getItem('loginId');
    const loginName = localStorage.getItem('loginName') || t('there');
    const deletePermission = localStorage.getItem('dataDeletePermission');

    const [concessionaires, setConcessionaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [query, setQuery] = useState('');

    // Tag body for v3 backdrop while this page is mounted. Mirrors the pattern
    // V3TenantDashboardPage uses for non-/v3 routes.
    useEffect(() => {
        document.body.classList.add('v3-themed');
        return () => {
            document.body.classList.remove('v3-themed');
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .post(`${config.hostname}/users/getconcessionaireaccess`, { user_id: login_id }, configheader)
            .then((response) => {
                if (cancelled) return;
                setConcessionaires(response.data.concessionaire_access || []);
                setLoading(false);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setErrorMsg(t('Could not load your malls. Please retry.'));
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return concessionaires;
        return concessionaires.filter((c) =>
            (c.concessionaire_name || '').toLowerCase().includes(q),
        );
    }, [query, concessionaires]);

    const totals = useMemo(() => {
        return concessionaires.reduce(
            (acc, c) => {
                acc.live += c.live_stores || 0;
                acc.disabled += c.disabled_stores || 0;
                acc.malls += 1;
                return acc;
            },
            { live: 0, disabled: 0, malls: 0 },
        );
    }, [concessionaires]);

    const handleCardClick = (c) => {
        localStorage.setItem('concessionaireId', c._id);
        localStorage.setItem('concessionaireName', c.concessionaire_name);
        localStorage.setItem('accessDashboard', '1');
        localStorage.setItem('accessAnalytics', '1');
        localStorage.setItem('accessReports', '1');
        localStorage.setItem('accessRevenueReports', '1');
        localStorage.setItem('accessDataGrid', '1');
        localStorage.setItem('accessGeneralSettings', '1');
        localStorage.setItem('manageGeneralSettings', '1');
        localStorage.setItem('accessTenants', '1');
        localStorage.setItem('manageTenants', '1');
        localStorage.setItem('manageConfigurations', '1');
        localStorage.setItem('manageUsers', '1');
        localStorage.setItem('manageUserProfile', '1');
        localStorage.setItem('accessLogs', '1');
        localStorage.setItem('manageScheduleReports', '1');
        localStorage.setItem('tenantPortalManage', '1');
        localStorage.setItem('accessHealthCheckup', '1');
        localStorage.setItem('dataMode', c.data_mode);
        localStorage.setItem('auditRequired', c.audit_required ? 'true' : 'false');
        localStorage.setItem('auditTolerance', c.audit_tolerance ?? '0');
        window.location.href = `${process.env.PUBLIC_URL}/admin/dashboard/sales/${layoutURL}`;
    };

    const handleViewAPIKey = () => {
        navigate(`${process.env.PUBLIC_URL}/admin/viewapikey/${layoutURL}`);
    };

    const handleDeleteData = () => {
        navigate(`${process.env.PUBLIC_URL}/admin/deletedata/${layoutURL}`);
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = `${process.env.PUBLIC_URL}/login`;
    };

    return (
        <div className="v3 v3-backend-home">
            <header className="v3-bh-hero">
                <div className="v3-bh-hero-text">
                    <div className="v3-label">{t('Control plane · Backend access')}</div>
                    <h1 className="v3-display v3-bh-greeting">
                        {t(greetingFor())},{' '}
                        <span className="v3-bh-name">{loginName}</span>
                    </h1>
                    <p className="v3-body v3-bh-sub">
                        {t('Pick a mall to drop into its workspace. Manage API keys and data tools from the toolbar.')}
                    </p>
                </div>

                <div className="v3-bh-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={handleViewAPIKey}>
                        <Key size={14} />
                        {t('View API key')}
                    </button>
                    {deletePermission === 'true' && (
                        <button type="button" className="v3-btn v3-bh-danger" onClick={handleDeleteData}>
                            <Trash2 size={14} />
                            {t('Delete data')}
                        </button>
                    )}
                    <button type="button" className="v3-btn v3-bh-logout" onClick={handleLogout}>
                        <LogOut size={14} />
                        {t('Logout')}
                    </button>
                </div>
            </header>

            <section className="v3-bh-stats">
                <div className="v3-card v3-bh-stat">
                    <span className="v3-label">{t('Malls')}</span>
                    <span className="v3-kpi">{totals.malls}</span>
                </div>
                <div className="v3-card v3-bh-stat">
                    <span className="v3-label">{t('Live stores')}</span>
                    <span className="v3-kpi v3-bh-stat-live">{totals.live}</span>
                </div>
                <div className="v3-card v3-bh-stat">
                    <span className="v3-label">{t('Disabled stores')}</span>
                    <span className="v3-kpi v3-bh-stat-disabled">{totals.disabled}</span>
                </div>
            </section>

            <section className="v3-bh-list-section">
                <div className="v3-bh-list-header">
                    <h2 className="v3-heading">{t('Your malls')}</h2>
                    <div className="v3-bh-search">
                        <Search size={14} aria-hidden />
                        <input
                            type="search"
                            placeholder={t('Search malls')}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label={t('Search malls')}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="v3-bh-skel-grid">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="v3-card v3-bh-skel" aria-hidden />
                        ))}
                    </div>
                ) : errorMsg ? (
                    <div className="v3-card v3-bh-empty v3-bh-error">{errorMsg}</div>
                ) : filtered.length === 0 ? (
                    <div className="v3-card v3-bh-empty">
                        <HomeIcon size={28} aria-hidden />
                        <h3 className="v3-title">
                            {concessionaires.length === 0 ? t('No malls assigned yet') : t('No matches')}
                        </h3>
                        <p className="v3-body">
                            {concessionaires.length === 0
                                ? t('Once a mall is granted to your account it will appear here.')
                                : t('Try a different search term.')}
                        </p>
                    </div>
                ) : (
                    <div className="v3-bh-grid">
                        {filtered.map((c) => (
                            <button
                                key={c._id}
                                type="button"
                                className="v3-card v3-bh-mall"
                                onClick={() => handleCardClick(c)}
                            >
                                <div className="v3-bh-mall-top">
                                    <div className="v3-bh-mall-icon" aria-hidden>
                                        <HomeIcon size={18} />
                                    </div>
                                    <span
                                        className={`v3-bh-mode v3-bh-mode-${(c.data_mode || 'live').toLowerCase()}`}
                                    >
                                        {(c.data_mode || 'live').toUpperCase()}
                                    </span>
                                </div>

                                <h3 className="v3-title v3-bh-mall-name">{c.concessionaire_name}</h3>

                                <div className="v3-bh-mall-meta">
                                    <span
                                        className={`v3-bh-audit ${c.audit_required ? 'on' : 'off'}`}
                                        title={
                                            c.audit_required
                                                ? t('Audit required · tolerance {{tolerance}}%', { tolerance: c.audit_tolerance ?? 0 })
                                                : t('Audit not required')
                                        }
                                    >
                                        {c.audit_required ? <Shield size={11} /> : <ShieldOff size={11} />}
                                        {c.audit_required ? t('Audit · {{tolerance}}%', { tolerance: c.audit_tolerance ?? 0 }) : t('No audit')}
                                    </span>
                                </div>

                                <div className="v3-bh-mall-stats">
                                    <div className="v3-bh-mall-stat">
                                        <span className="dot live" aria-hidden />
                                        <span className="num">{c.live_stores ?? 0}</span>
                                        <span className="lbl">{t('live')}</span>
                                    </div>
                                    <div className="v3-bh-mall-stat">
                                        <span className="dot disabled" aria-hidden />
                                        <span className="num">{c.disabled_stores ?? 0}</span>
                                        <span className="lbl">{t('disabled')}</span>
                                    </div>
                                    <div className="v3-bh-mall-chevron" aria-hidden>
                                        <ChevronRight size={16} />
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default BackendHomeView;
